import React from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon, Badge as BadgeIcon } from '@mui/icons-material';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Customer } from '../../types';
import IdentityAvatar from '../common/IdentityAvatar';
import CompanyNameCell from '../common/CompanyNameCell';
import CustomerActionsMenu from './CustomerActionsMenu';
import { createCustomerNameActionSx } from '../../theme/interactions';
import type { Language } from '../../contexts/LanguageContext';
import { getPersonInitials } from '../../utils/initials';

interface CustomerColumnOptions {
  t: (key: string) => string;
  language: Language;
  canWrite: boolean;
  isSuperAdmin: boolean;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
}

export function createCustomerColumns(options: CustomerColumnOptions): ColumnDef<Customer>[] {
  const { t, language, canWrite, isSuperAdmin, onEdit, onDelete, onCustomerClick } = options;
  return [
    { accessorKey: 'firstName', header: t('customers.firstName'), cell: ({ row }: CellContext<Customer, unknown>) => {
      const customerName = `${row.original.firstName} ${row.original.lastName}`;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <IdentityAvatar>{getPersonInitials(row.original.firstName, row.original.lastName, language) || <PersonIcon fontSize="small" />}</IdentityAvatar>
          {onCustomerClick ? (
            <Button
              aria-label={`${t('debts.openCustomerAccount')}: ${customerName}`}
              onClick={() => onCustomerClick(row.original.id, customerName)}
              sx={theme => ({ p: 0, minWidth: 0, minHeight: 0, textAlign: 'left', textTransform: 'none', ...createCustomerNameActionSx(theme) })}
            >
              {customerName}
            </Button>
          ) : <Typography variant="body2">{customerName}</Typography>}
        </Box>
      );
    } },
    { accessorKey: 'phone', header: t('customers.phone'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon fontSize="small" color="action" /><Typography variant="body2">{row.original.phone || '-'}</Typography></Box> },
    { accessorKey: 'email', header: t('customers.email'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}><EmailIcon fontSize="small" color="action" /><Typography variant="body2" noWrap>{row.original.email || '-'}</Typography></Box> },
    { accessorKey: 'taxIdentificationNumber', header: t('customers.taxIdentificationNumber'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BadgeIcon fontSize="small" color="action" /><Typography variant="body2">{row.original.taxIdentificationNumber || '-'}</Typography></Box> },
    { accessorKey: 'address', header: t('customers.address'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => (
      <Tooltip title={row.original.address || ''} placement="top-start">
        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>{row.original.address}</Typography>
      </Tooltip>
    ) },
    ...(isSuperAdmin ? [{ accessorKey: 'companyName', header: t('customers.company'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => <CompanyNameCell name={row.original.companyName || row.original.company?.name} /> }] : []),
    ...(canWrite ? [{ id: 'actions', header: t('common.actions'), enableSorting: false, cell: ({ row }: CellContext<Customer, unknown>) => <CustomerActionsMenu customer={row.original} onEdit={onEdit} onDelete={onDelete} /> }] : []),
  ];
}
